import { useQuery } from "@tanstack/react-query";
import {
  loadLocationDetails,
  syncAccessibility,
  syncAccessibilityEnterprise,
  syncAccessibilityHybrid,
} from "../../../apis/venueDetails";
import {
  AccessibilityCategory,
  AccessibilityCategoryHybrid,
  BusinessNote,
  BusinessNoteAction,
  BusinessNoteTask,
  BusinessSociabilitySaysApproved,
  ComplexCategoriesData,
  VenueDetails,
} from "../../../types";

type UseVenueDetailsBySlugProps = {
  slug: string;
  latitude: string;
  longitude: string;
  accessToken: string;
  enabled: boolean;
};

export type VenueDetailsBySlugResponse = {
  details: VenueDetails;
  accesibilityCategories: AccessibilityCategory[];
  accesibilityCategoriesHybrid: AccessibilityCategoryHybrid[];
  accesibilityCategoriesEnterprise: ComplexCategoriesData[];
  floorTypeOptions: { id: number; title: string }[];
  enterpriseBusinessNotesActions: BusinessNoteAction[];
  businessNotesTasks: {
    Custom: BusinessNoteTask[];
    General: BusinessNoteTask[];
  };
  review_tags: ComplexCategoriesData[];
  businessNotesAreaLocationTypes: string[];
  businessNotesForMappers: BusinessNote[];
  businessSociabilitySaysApproved: BusinessSociabilitySaysApproved[];
};

export function useVenueDetailsBySlug({
  slug,
  latitude,
  longitude,
  accessToken,
  enabled,
}: UseVenueDetailsBySlugProps) {
  const { data, error, isLoading, isFetching, refetch } = useQuery({
    queryKey: [
      "loadVenueDetailsBySlug",
      slug,
      latitude,
      longitude,
      accessToken,
    ],
    queryFn: async () => {
      console.log("slug", slug);
      const detailsResponse = await loadLocationDetails(slug, {
        latitude: latitude,
        longitude: longitude,
        token: accessToken,
      });

      let accesibilityCategoriesHybrid = [];
      let accesibilityCategories = [];

      if (
        detailsResponse.templateType != "Basic" &&
        detailsResponse.isPublicTemplate === "Yes"
      ) {
        accesibilityCategoriesHybrid = await syncAccessibilityHybrid(
          detailsResponse.business.id,
          slug
        );
        return {
          details: detailsResponse,
          accesibilityCategoriesHybrid,
        } as VenueDetailsBySlugResponse;
      } else if (
        detailsResponse.templateType != "Basic" &&
        detailsResponse.isPublicTemplate === "No"
      ) {
        const {
          accesibilityCategoriesEnterprise,
          floorTypeOptions,
          enterpriseBusinessNotesActions,
          businessNotesTasks,
          review_tags,
          businessNotesAreaLocationTypes,
          businessNotesForMappers,
          businessSociabilitySaysApproved
        } = await syncAccessibilityEnterprise(
          detailsResponse.business.id,
          slug
        );
        return {
          details: detailsResponse,
          accesibilityCategoriesEnterprise,
          floorTypeOptions,
          enterpriseBusinessNotesActions,
          businessNotesTasks,
          review_tags,
          businessNotesAreaLocationTypes,
          businessNotesForMappers,
          businessSociabilitySaysApproved 
        } as VenueDetailsBySlugResponse;
      } else {
        accesibilityCategories = await syncAccessibility(
          detailsResponse.business.id,
          slug
        );
        return {
          details: detailsResponse,
          accesibilityCategories,
        } as VenueDetailsBySlugResponse;
      }
    },
    enabled,
    staleTime: 10000,
  });
  return {
    data,
    error,
    isLoading: isLoading || isFetching,
    refetch,
  };
}
